"use client";

import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Button } from "../../atoms/button/button";
import type { ButtonVariant } from "../../atoms/button/button.shared";
import { Tag } from "../../atoms/tag/tag";
import {
  TEXT_BODY_ELEMENTS,
  TEXT_BODY_VARIANTS,
  TEXT_HEADING_VARIANTS,
  TEXT_LEVELS,
  Text,
  safeElement,
  safeVariant,
  type TextHeadingVariant,
  type TextLevel
} from "../../atoms/text/text";
import { Modal } from "../../molecules/modal/modal";
import { ctaAriaLabel } from "../../services/cta-aria-label";
import { PriceBlock } from "../../molecules/price-block/price-block";
import {
  analyticsText,
  firePageView,
  fireQuizQuestionStatus,
  fireQuizRecommendationStatus,
  fireQuizStatus,
  fireSelectItem,
  linkClickAttrs,
  type LinkClickAttrs
} from "../../services/analytics";
import { run, visibleQuestions } from "./engine/engine";
import type { Answers, Recipe, RecipeQuestion, RunResult } from "./engine/engine.types";
import { buildBroadCtaUrl, buildFocusedCtaUrl } from "./utils/build-cta";
import {
  ITEM_LIST_NAME,
  QUIZ_NAME,
  absoluteCtaUrl,
  broadItemId,
  clearSession,
  ctaVariant,
  currentPageUrl,
  focusedItemId,
  fmtPrice,
  isRestorableSession,
  navLinkPosition,
  nextTierLine,
  optionLinkPosition,
  priceSuffix,
  quizLinkPosition,
  quizVirtualUrl,
  readSession,
  recommendationLinkPosition,
  safeCtaUrl,
  splitPrice,
  storageKey,
  writeSession,
  type PriceCopy
} from "./product-recommender.helpers";
import type {
  PricedCatalog,
  PricedCatalogPackage,
  ProductRecommenderProps,
  ResolvedPriceTier
} from "./product-recommender.types";
import styles from "./product-recommender.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/* Client on the no-zero-JavaScript-form argument, recorded on
   design-conflicts/product-recommender.mdx. */

export type ProductRecommenderClientProps = Omit<
  ProductRecommenderProps,
  "recipe" | "catalog" | "messages" | "background_desktop_url" | "background_mobile_url" | "isAboveTheFold"
> & {
  recipe: Recipe;
  catalog: PricedCatalog;
  /** The server-rendered channel-logo backdrop, drawn behind the banner and the quiz. */
  backdrop: React.ReactNode;
};

type Tracking = Partial<LinkClickAttrs>;

const DEFAULT_SECONDARY_CTA_LABEL = "See all packages";
/* The dialog's name when both headings are blank — never an empty aria-label. */
const DEFAULT_DIALOG_LABEL = "package-recommender";

/* Element ids carry the blok scope as well as the question id: two blocks
   sharing a recipe on one page would otherwise mint the same ids. */
const domId = (scope: string, kind: string, questionId: string) => `pr-${scope}-${kind}-${questionId}`;

/** The attributes GTM reads, on a box-less wrapper: Button takes no `data-*` attributes. */
function TrackingSlot({ tracking, children }: { tracking: Tracking; children: React.ReactNode }) {
  return (
    <span
      className={styles.trackingSlot}
      data-linkdestination={tracking["data-linkdestination"]}
      data-linkname={tracking["data-linkname"]}
      data-linkposition={tracking["data-linkposition"]}
    >
      {children}
    </span>
  );
}

function CheckIcon() {
  return (
    <svg aria-hidden="true" className={styles.checkIcon} focusable="false" viewBox="0 0 24 24">
      <path
        d="M9.5923 18.4104C9.81569 18.6538 10.1396 18.7203 10.4195 18.6239H10.422C10.425 18.6229 10.4292 18.6217 10.4339 18.6199L10.4352 18.6193C10.4758 18.6043 10.5119 18.5857 10.5446 18.5675L10.5452 18.5682C10.5452 18.5682 10.5476 18.5676 10.549 18.5669C10.5541 18.5642 10.566 18.5575 10.5798 18.549L10.581 18.5483C10.6241 18.5212 10.6779 18.4834 10.73 18.4303C10.7387 18.4213 10.7458 18.4129 10.7507 18.4071C10.7554 18.4014 10.7592 18.3965 10.7608 18.3945L10.762 18.3931L10.7646 18.3898L10.7671 18.3872C10.7709 18.3827 10.7768 18.3754 10.7838 18.3667L10.7865 18.3633L10.7891 18.3606C10.7903 18.3592 10.7919 18.3566 10.7941 18.354C10.7984 18.3489 10.805 18.3409 10.8123 18.3308L19.1696 6.69434C19.421 6.3442 19.3761 5.85422 19.0778 5.561L19.0143 5.5053C18.6604 5.22235 18.1558 5.29547 17.8873 5.6691L10.0951 16.517L6.04913 12.1057C5.75952 11.7899 5.29388 11.7626 4.97367 12.0288L4.91145 12.0858C4.59219 12.4118 4.58367 12.9492 4.89259 13.2861L9.5923 18.4104Z"
        fill="currentColor"
      />
    </svg>
  );
}

/* Figma's skip_back glyph, which no DDS icon covers. */
function StartOverIcon() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 12.6 12.75">
      <path
        d="M0.223036 2.37909L0.955024 2.76664C1.60746 1.81632 2.50541 1.0607 3.55322 0.580204C4.60103 0.0997099 5.75947 -0.0876743 6.90533 0.0380018C8.05118 0.163678 9.14152 0.59771 10.0603 1.2939C10.979 1.99008 11.6918 2.92236 12.1228 3.99151C12.5537 5.06065 12.6867 6.22664 12.5075 7.36537C12.3284 8.50409 11.8439 9.57292 11.1055 10.4581C10.3671 11.3433 9.40243 12.0117 8.31429 12.3921C7.22616 12.7726 6.05526 12.8509 4.92616 12.6186C3.86936 12.4039 2.88468 11.9236 2.06494 11.2229C1.2452 10.5222 0.617426 9.62425 0.240831 8.61375C0.137275 8.33864 0.0288084 7.92397 0.0288084 7.92397C0.0233982 7.90377 0.0221412 7.88267 0.0251191 7.86196C0.028097 7.84126 0.035284 7.82136 0.0461695 7.8035C0.057055 7.78563 0.0714502 7.77016 0.0884872 7.75802C0.105524 7.74588 0.124796 7.73733 0.145236 7.73287L1.13992 7.51064C1.18202 7.50185 1.22591 7.50941 1.26264 7.53178C1.29937 7.55415 1.32622 7.58965 1.33773 7.63109C1.33773 7.63109 1.41459 7.92531 1.49636 8.1422C1.88321 9.17341 2.59768 10.0493 3.53011 10.6355C4.46254 11.2217 5.56148 11.4859 6.65847 11.3875C7.75547 11.2892 8.78995 10.8337 9.60324 10.091C10.4165 9.34832 10.9637 8.35935 11.161 7.27577C11.3582 6.19218 11.1946 5.07381 10.6953 4.09214C10.1959 3.11047 9.38828 2.31969 8.39632 1.84108C7.40436 1.36246 6.28281 1.22241 5.20361 1.44242C4.12442 1.66243 3.14715 2.23035 2.42171 3.05909C2.31327 3.18309 2.21323 3.31331 2.11768 3.44664L2.80659 3.85953C2.94392 3.93997 2.93727 4.0582 2.79194 4.12264L2.34749 4.31953L0.51633 5.09242C0.493932 5.10494 0.468748 5.11141 0.443088 5.11118C0.417428 5.11094 0.392231 5.10402 0.370063 5.0911C0.347894 5.07817 0.329521 5.0597 0.316677 5.03748C0.303834 5.01526 0.296959 4.99008 0.296821 4.96442L0.289659 4.89198L0.204373 4.01686L0.0487736 3.0062L0.00168163 2.5222C-0.0143184 2.36264 0.0857025 2.29864 0.223036 2.37909Z"
        fill="currentColor"
      />
    </svg>
  );
}

type SecondaryAction = { label: string; ariaLabel?: string; onClick: () => void };

type QuestionStepProps = {
  scope: string;
  question: RecipeQuestion;
  selected: string[];
  progressText: string;
  selectHelper: string;
  questionLevel: TextLevel;
  questionVariant: TextHeadingVariant;
  advanceLabel: string;
  advanceAriaLabel?: string;
  advanceVariant: ButtonVariant;
  secondary?: SecondaryAction;
  secondaryVariant: ButtonVariant;
  headingRef: React.RefObject<HTMLDivElement>;
  /** One position per question, one name per option. */
  optionTracking: (linkName: string) => Tracking;
  /** Shared by Next, Back and Close; each reports its own name. */
  navTracking: (linkName: string) => Tracking;
  onToggle: (optionId: string) => void;
  onNext: () => void;
};

const ARROW_KEYS = ["ArrowDown", "ArrowRight", "ArrowUp", "ArrowLeft"];

/* A native radio re-picks, and reports, on every auto-repeat and Shift+arrow.
   Ctrl, Alt and Meta are left alone so browser shortcuts such as Alt+Left work. */
const ignoreRepeatOrShiftArrow = (event: React.KeyboardEvent<HTMLInputElement>) => {
  if ((event.repeat || event.shiftKey) && ARROW_KEYS.includes(event.key)) {
    event.preventDefault();
  }
};

function QuestionStep({
  scope,
  question,
  selected,
  progressText,
  selectHelper,
  questionLevel,
  questionVariant,
  advanceLabel,
  advanceAriaLabel,
  advanceVariant,
  secondary,
  secondaryVariant,
  headingRef,
  optionTracking,
  navTracking,
  onToggle,
  onNext
}: QuestionStepProps) {
  const single = question.mode === "single";
  /* Only picks the current recipe still offers count: a stale id must not
     enable Next while every option renders unchecked. */
  const pickedCount = question.options.filter(option => selected.includes(option.id)).length;
  const canNext = single ? pickedCount === 1 : pickedCount > 0;
  const labelId = domId(scope, "label", question.id);
  const helperId = domId(scope, "help", question.id);
  const secondaryTracking = secondary ? navTracking(secondary.label) : {};
  const advanceTracking = navTracking(advanceLabel);

  return (
    <div className={styles.step}>
      <div className={styles.stepBody}>
        <div className={styles.stepHeader}>
          {/* Hidden because the heading below already carries it in its name. */}
          <p aria-hidden="true" className={`dds-text dds-body-3 dds-weight-medium ${styles.progress}`}>
            {progressText}
          </p>
          {/* The focus target (Text takes no ref). Progress is in the heading's name
              but not the group's, so it is not re-read on every arrow. */}
          <div ref={headingRef} tabIndex={-1}>
            <Text as={questionLevel} id={domId(scope, "q", question.id)} variant={questionVariant}>
              <span className="dds-sr-only">{`${progressText}. `}</span>
              <span id={labelId}>{question.label}</span>
            </Text>
          </div>
        </div>
        <fieldset
          aria-describedby={selectHelper ? helperId : undefined}
          aria-labelledby={labelId}
          className={styles.answers}
        >
          {selectHelper && (
            <p className={`dds-text dds-body-3 dds-weight-medium ${styles.helper}`} id={helperId}>
              {selectHelper}
            </p>
          )}
          <div className={styles.options}>
            {question.options.map(option => (
              <label className={styles.option} key={option.id}>
                {/* GTM names a click from aria-label, not data-linkname. Case is the
                    only difference from the visible label. */}
                <input
                  {...optionTracking(option.label)}
                  aria-label={analyticsText(option.label) || undefined}
                  checked={selected.includes(option.id)}
                  className={styles.optionInput}
                  name={domId(scope, "opts", question.id)}
                  onChange={() => onToggle(option.id)}
                  onKeyDown={single ? ignoreRepeatOrShiftArrow : undefined}
                  type={single ? "radio" : "checkbox"}
                  value={option.id}
                />
                <span className={styles.face}>
                  <span className={styles.optionLabel}>{option.label}</span>
                  <span aria-hidden="true" className={styles.optionCheck}>
                    <CheckIcon />
                  </span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      </div>
      <div className={styles.stepActions}>
        {secondary && (
          <TrackingSlot tracking={secondaryTracking}>
            <Button
              ariaLabel={secondary.ariaLabel}
              className={styles.stepCta}
              onClick={secondary.onClick}
              size="medium"
              variant={secondaryVariant}
            >
              {secondary.label}
            </Button>
          </TrackingSlot>
        )}
        <TrackingSlot tracking={advanceTracking}>
          <Button
            ariaLabel={advanceAriaLabel}
            className={styles.stepCta}
            disabled={!canNext}
            onClick={onNext}
            size="medium"
            variant={advanceVariant}
          >
            {advanceLabel}
          </Button>
        </TrackingSlot>
      </div>
    </div>
  );
}

type ResultCardProps = {
  scope: string;
  cardKey: string;
  title: string;
  titleLevel: TextLevel;
  titleVariant: TextHeadingVariant;
  price: number | null;
  regularPrice: number | null;
  promoMonths: number | null;
  nextTier: ResolvedPriceTier | null;
  promoKnown: boolean;
  channels: string | null;
  priceCopy: PriceCopy;
  legal?: string;
  ctaUrl: string | null;
  ctaLabel: string;
  ctaAriaLabel?: string;
  primaryVariant: ButtonVariant;
  ctaTracking: Tracking;
  tagLabel?: string;
  secondaryCta?: { label: string; ariaLabel?: string; url: string; variant: ButtonVariant; tracking: Tracking };
  onCtaClick: () => void;
};

function ResultCard({
  scope,
  cardKey,
  title,
  titleLevel,
  titleVariant,
  price,
  regularPrice,
  promoMonths,
  nextTier,
  promoKnown,
  channels,
  priceCopy,
  legal,
  ctaUrl,
  ctaLabel,
  ctaAriaLabel: primaryAriaLabel,
  primaryVariant,
  ctaTracking,
  tagLabel,
  secondaryCta,
  onCtaClick
}: ResultCardProps) {
  /* Number.isFinite rather than `!= null`: a hand-authored feed can carry
     "64.99" as a string, and .toFixed on it would throw with no boundary above. */
  const parts = Number.isFinite(price) ? splitPrice(price) : null;
  const secondLine = nextTierLine(nextTier, promoMonths, priceCopy);
  /* A struck price says the shown one is promotional, so with no stated window
     the "+ tax" permanence claim is withheld exactly as an unknown promo is. */
  const strikesPrice = Number.isFinite(regularPrice) && Number.isFinite(price) && regularPrice > price;
  const hasWindow = promoMonths != null && promoMonths > 0;
  const suffix = priceSuffix(promoMonths, !!secondLine, promoKnown && !(strikesPrice && !hasWindow), priceCopy);
  const titleId = domId(scope, "card", cardKey);

  return (
    <div aria-labelledby={titleId} className={styles.card} role="group">
      <div className={styles.cardHeading}>
        <Text as={titleLevel} id={titleId} variant={titleVariant} weight="medium">
          {title}
        </Text>
        {channels && (
          <Text as="p" tone="tertiary" variant="body-3">
            {channels}
          </Text>
        )}
      </div>
      {tagLabel && (
        <div className={styles.cardTag}>
          <Tag color="yellow">{tagLabel}</Tag>
        </div>
      )}
      {/* The disclosure only qualifies a figure, so without one it is not shown. */}
      {parts && (
        <PriceBlock
          cents={parts.cents}
          className={styles.cardPrice}
          dollars={parts.dollars}
          frequency={suffix}
          legal={secondLine || legal ? (
            <>
              {secondLine && <span className={styles.nextTier}>{secondLine}</span>}
              {legal}
            </>
          ) : undefined}
          strikethroughPrice={strikesPrice ? fmtPrice(regularPrice) : undefined}
        />
      )}
      {/* A card with no price offers no links at all — a working Shop button
          under no figure reads as an offer at an undisclosed price. */}
      {ctaUrl && parts && (
        <div className={styles.cardActions}>
          <TrackingSlot tracking={ctaTracking}>
            <Button ariaLabel={primaryAriaLabel} href={ctaUrl} onClick={onCtaClick} size="small" variant={primaryVariant}>
              {ctaLabel}
            </Button>
          </TrackingSlot>
          {secondaryCta && (
            <TrackingSlot tracking={secondaryCta.tracking}>
              <Button ariaLabel={secondaryCta.ariaLabel} href={secondaryCta.url} size="small" variant={secondaryCta.variant}>
                {secondaryCta.label}
              </Button>
            </TrackingSlot>
          )}
        </div>
      )}
    </div>
  );
}

type CardMeta = {
  promoMonths: number | null;
  nextTier: ResolvedPriceTier | null;
  promoKnown: boolean;
  channels: string | null;
};

/* promoKnown is opt-in: with no information the weaker claim has to win, so
   only an explicit true lets the card say "+ tax". */
const cardMetaOf = (pack?: PricedCatalogPackage | null): CardMeta => ({
  promoMonths: pack?.promoDuration ?? null,
  nextTier: pack?.nextTier ?? null,
  promoKnown: pack?.promoKnown === true,
  channels: pack?.channels ?? null
});

type OrderedCard = CardMeta & {
  key: "broad" | "focused";
  title: string;
  price: number | null;
  regularPrice: number | null;
  ctaUrl: string | null;
  analyticsId: string;
};

/** The cards the results screen shows, in display order — also what is reported. */
const orderCards = (result: RunResult | null, catalog: PricedCatalog): OrderedCard[] => {
  if (!result) {
    return [];
  }
  const broadPack = catalog.signatures?.find(pack => pack.id === result.broad?.id) ??
    (catalog.spanishSignature?.id === result.broad?.id ? catalog.spanishSignature : null);
  const broadCard: OrderedCard = {
    key: "broad",
    title: result.broadLabel,
    price: result.broadPrice,
    regularPrice: result.broad ? result.broad.regularPrice ?? null : null,
    ...cardMetaOf(broadPack),
    ctaUrl: safeCtaUrl(buildBroadCtaUrl(result.broad)),
    analyticsId: broadItemId(result.broad?.slug)
  };
  /* The engine emits at most one focused id, so the pack's own display fields
     describe the whole card. */
  const focusedPack = catalog.genrePacks?.find(pack => pack.id === result.focusedIds[0]);
  const focusedCard: OrderedCard = {
    key: "focused",
    title: result.focusedLabels.join(" + "),
    price: result.focusedPrice,
    regularPrice: focusedPack?.regularPrice ?? null,
    ...cardMetaOf(focusedPack),
    ctaUrl: safeCtaUrl(buildFocusedCtaUrl(result.focusedIds, catalog)),
    analyticsId: focusedItemId(result.focusedIds.map(id => catalog.genrePacks?.find(pack => pack.id === id)?.shopCode))
  };
  const lead = result.primary === "focused" ? focusedCard : broadCard;
  const alternative = result.primary === "focused" ? broadCard : focusedCard;
  /* Never a card without a live price: a non-finite runner-up is dropped. */
  return result.showAlternative && Number.isFinite(alternative.price) ? [lead, alternative] : [lead];
};

type ReportingInput = {
  restored: boolean;
  modalOpenedByShopper: boolean;
  quizVisible: boolean;
  resultsVisible: boolean;
  questionId?: string;
  questionLabel?: string;
  cursor: number;
  progressLabel: string;
  recommendedItemIds: string[];
};

/** The quiz's own events, fired in declaration order. Returns the arm for
    `complete`, so a restored or reopened results screen completes nothing. */
function useQuizReporting({
  restored,
  modalOpenedByShopper,
  quizVisible,
  resultsVisible,
  questionId,
  questionLabel,
  cursor,
  progressLabel,
  recommendedItemIds
}: ReportingInput): () => void {
  useEffect(() => {
    if (!modalOpenedByShopper) {
      return;
    }
    firePageView({
      linkPosition: quizLinkPosition(true),
      linkDestination: quizVirtualUrl()
    });
  }, [modalOpenedByShopper]);

  /* One start per stretch of the quiz being on screen: a reopen and Start over
     both start again, which keeps start and complete countable against each other. */
  const startFiredRef = useRef(false);
  useEffect(() => {
    if (!restored || !quizVisible) {
      startFiredRef.current = false;
      return;
    }
    if (startFiredRef.current) {
      return;
    }
    startFiredRef.current = true;
    fireQuizStatus({
      status: "start",
      quizName: QUIZ_NAME
    });
  }, [restored, quizVisible]);

  /* Read, not depended on: a conditional question appearing moves "2 of 3" to
     "2 of 4" without navigating, and must not re-report the question. */
  const progressLabelRef = useRef(progressLabel);
  useEffect(() => {
    progressLabelRef.current = progressLabel;
  }, [progressLabel]);

  /* Keyed on the step as well as the question, so Back re-reports. */
  useEffect(() => {
    if (!restored || !quizVisible || !questionId) {
      return;
    }
    fireQuizQuestionStatus({
      status: "displayed",
      progressLabel: progressLabelRef.current,
      questionLabel
    });
  }, [restored, quizVisible, questionId, questionLabel, cursor]);

  const pendingCompleteRef = useRef(false);
  const resultsFiredRef = useRef(false);
  /* `complete` must precede the recommendation; the recommendation is an
     impression, so it reports on every results screen. */
  useEffect(() => {
    if (!restored || !resultsVisible) {
      resultsFiredRef.current = false;
      return;
    }
    if (resultsFiredRef.current) {
      return;
    }
    resultsFiredRef.current = true;
    if (pendingCompleteRef.current) {
      pendingCompleteRef.current = false;
      fireQuizStatus({
        status: "complete",
        quizName: QUIZ_NAME
      });
    }
    fireQuizRecommendationStatus({ itemIds: recommendedItemIds });
  }, [restored, resultsVisible, recommendedItemIds]);

  return () => {
    pendingCompleteRef.current = true;
  };
}

function ProductRecommenderClientImpl({
  id,
  entry_mode: entryMode = "modal",
  heading_content: heading,
  heading_level: headingLevel,
  heading_variant: headingVariant,
  subheading_content: subhead,
  subheading_element: subheadElement,
  subheading_variant: subheadVariant,
  entry_cta_label: entryCtaLabel = "Get started",
  entry_cta_variant: entryCtaVariant,
  entry_cta_aria_label: entryCtaAriaLabel,
  question_level: questionLevel,
  question_variant: questionVariant,
  results_heading_content: resultsHeading,
  results_heading_level: resultsHeadingLevel,
  results_heading_variant: resultsHeadingVariant,
  title_level: titleLevel,
  title_variant: titleVariant,
  next_cta_label: nextLabel = "Next",
  next_cta_variant: nextCtaVariant,
  next_cta_aria_label: nextCtaAriaLabel,
  back_cta_label: backLabel = "Back",
  back_cta_variant: backCtaVariant,
  back_cta_aria_label: backCtaAriaLabel,
  close_cta_label: closeLabel = "Close",
  close_cta_aria_label: closeCtaAriaLabel,
  select_one_label: selectOneLabel = "Select one",
  select_all_label: selectAllLabel = "Select all that apply",
  results_cta_label: resultsCtaLabel = "See my results",
  results_cta_aria_label: resultsCtaAriaLabel,
  primary_cta_label: primaryCtaLabel = "Shop now",
  primary_cta_variant: primaryCtaVariant,
  primary_cta_aria_label: primaryCtaAriaLabel,
  primary_tag_label: primaryTagLabel = "Our pick for you",
  start_over_cta_label: startOverLabel = "Start over",
  start_over_cta_variant: startOverCtaVariant,
  start_over_cta_aria_label: startOverCtaAriaLabel,
  price_legal_content: priceLegal,
  price_promo_content: priceCopyPromo,
  price_ongoing_content: priceCopyOngoing,
  price_next_tier_content: priceCopyNextTier,
  secondary_cta_signature_label: secondaryCtaSignatureLabel,
  secondary_cta_signature_url: secondaryCtaSignatureUrl,
  secondary_cta_signature_aria_label: secondaryCtaSignatureAriaLabel,
  secondary_cta_genre_label: secondaryCtaGenreLabel,
  secondary_cta_genre_url: secondaryCtaGenreUrl,
  secondary_cta_genre_aria_label: secondaryCtaGenreAriaLabel,
  secondary_cta_variant: secondaryCtaVariant,
  recipe,
  catalog,
  backdrop
}: ProductRecommenderClientProps) {
  /* Only "inline" opts out, so an unrecognised value falls back to the
     declared default rather than to the inline shell. */
  const isModal = entryMode !== "inline";
  const headingAs = safeElement(headingLevel, "h2", TEXT_LEVELS);
  const headingStyle = safeVariant(headingVariant, "subhead-1", TEXT_HEADING_VARIANTS);
  const subheadAs = safeElement(subheadElement, "p", TEXT_BODY_ELEMENTS);
  const subheadStyle = safeVariant(subheadVariant, "body-1", TEXT_BODY_VARIANTS);
  const questionAs = safeElement(questionLevel, "h3", TEXT_LEVELS);
  const questionStyle = safeVariant(questionVariant, "headline-2", TEXT_HEADING_VARIANTS);
  const resultsHeadingAs = safeElement(resultsHeadingLevel, "h2", TEXT_LEVELS);
  const resultsHeadingStyle = safeVariant(resultsHeadingVariant, "headline-2", TEXT_HEADING_VARIANTS);
  const titleAs = safeElement(titleLevel, "h3", TEXT_LEVELS);
  const titleStyle = safeVariant(titleVariant, "subhead-2", TEXT_HEADING_VARIANTS);
  const entryTreatment = ctaVariant(entryCtaVariant, "primary");
  const nextTreatment = ctaVariant(nextCtaVariant, "primary");
  const backTreatment = ctaVariant(backCtaVariant, "tonal");
  const primaryTreatment = ctaVariant(primaryCtaVariant, "primary");
  const secondaryTreatment = ctaVariant(secondaryCtaVariant, "secondary");
  const startOverTreatment = ctaVariant(startOverCtaVariant, "tonal");
  const closeAriaLabel = ctaAriaLabel(closeCtaAriaLabel);
  const sessionKey = storageKey(id);
  const generatedScope = useId();
  const scope = id || generatedScope;

  const [modalOpen, setModalOpen] = useState(false);
  /* Inline: the entry button swaps the quiz card in and the banner never returns. */
  const [inlineStarted, setInlineStarted] = useState(false);
  const [phase, setPhase] = useState<"ask" | "results">("ask");
  const [answers, setAnswers] = useState<Answers>({});
  const [cursor, setCursor] = useState(0);
  /* Set in an effect, so the first client render matches the server's: nothing
     is reported, and no `window`-derived tracking attribute renders, before it. */
  const [restored, setRestored] = useState(false);
  const headingRef = useRef<HTMLDivElement>(null);
  const resultsHeadingRef = useRef<HTMLDivElement>(null);
  const prevCursorRef = useRef<number | null>(null);
  /* A pressed button unmounts with its screen, so the next screen's heading
     takes focus. Only a shopper's press arms these; a restored run does not. */
  const focusQuestionRef = useRef(false);
  const focusResultsRef = useRef(false);

  /* Only a completed run is replayed; anything else stored is purged. */
  useEffect(() => {
    const saved = readSession(sessionKey);
    if (isRestorableSession(saved)) {
      setAnswers(saved.answers);
      setPhase("results");
    } else if (saved) {
      clearSession(sessionKey);
    }
    setRestored(true);
  }, [sessionKey]);

  /* Mid-quiz progress must not outlive the visit, so only results are written. */
  useEffect(() => {
    if (phase !== "results") {
      return;
    }
    writeSession(sessionKey, {
      answers,
      phase
    });
  }, [sessionKey, answers, phase]);

  const visible = visibleQuestions(recipe, answers);
  const clampedCursor = Math.max(0, Math.min(cursor, visible.length - 1));
  const current = visible[clampedCursor];
  const isLast = clampedCursor === visible.length - 1;
  /* One string for the step indicator and the reported number, so the report
     cannot disagree with what the shopper saw. */
  const progressLabel = `${clampedCursor + 1} of ${visible.length}`;

  /* run() throws on a catalog-contract breach. Recorded here and handled in an
     effect below, because a memo body must stay free of side effects. */
  const engineOutcome = useMemo<{ result: RunResult | null; failed: boolean; error?: unknown }>(() => {
    if (phase !== "results") {
      return {
        result: null,
        failed: false
      };
    }
    try {
      return {
        result: run(answers, {
          recipe,
          catalog
        }),
        failed: false
      };
    } catch (error) {
      return {
        result: null,
        failed: true,
        error
      };
    }
  }, [phase, answers, recipe, catalog]);
  const { result, failed: engineFailed, error: engineError } = engineOutcome;

  /* Held so the recommendation event has a stable dependency and reports
     exactly the cards on screen. */
  const orderedCards = useMemo(() => orderCards(result, catalog), [result, catalog]);
  const recommendedItemIds = useMemo(() => orderedCards.map(card => card.analyticsId), [orderedCards]);

  /* Resolved once for the visible heading and the dialog name, which must agree. */
  const resultsTitle = (resultsHeading || "").trim() || (heading || "").trim();
  const headingLabel = phase === "results" ? resultsTitle : (heading || "").trim() || (resultsHeading || "").trim();
  const dialogLabel = headingLabel || DEFAULT_DIALOG_LABEL;

  /* A restored completed run lands on its results with no banner left to click. */
  const inlineOpen = inlineStarted || (restored && phase === "results");
  const shellOpen = isModal ? modalOpen : inlineOpen;
  const quizVisible = !engineFailed && phase === "ask" && !!current && shellOpen;
  const resultsVisible = !engineFailed && phase === "results" && !!result && shellOpen;

  const armComplete = useQuizReporting({
    restored,
    modalOpenedByShopper: isModal && modalOpen,
    quizVisible,
    resultsVisible,
    questionId: current?.id,
    questionLabel: current?.label,
    cursor: clampedCursor,
    progressLabel,
    recommendedItemIds
  });

  /* Focus the question on a step change, and on a first paint a shopper
     caused — the inline card opening, or Start over. */
  useEffect(() => {
    if (!quizVisible) {
      return;
    }
    if (focusQuestionRef.current || (prevCursorRef.current !== null && prevCursorRef.current !== clampedCursor)) {
      focusQuestionRef.current = false;
      headingRef.current?.focus();
    }
    prevCursorRef.current = clampedCursor;
  }, [quizVisible, clampedCursor]);

  useEffect(() => {
    if (resultsVisible && focusResultsRef.current) {
      focusResultsRef.current = false;
      resultsHeadingRef.current?.focus();
    }
  }, [resultsVisible]);

  /* A breach is not an authoring state, so it logs at error level, and the
     stored run is cleared so a refresh does not replay into the same throw. */
  useEffect(() => {
    if (engineFailed) {
      console.error("[ProductRecommender] hidden — engine rejected the catalog/recipe", engineError);
      clearSession(sessionKey);
    }
  }, [engineFailed, engineError, sessionKey]);

  if (engineFailed) {
    return null;
  }

  const tracked = (linkName: string, linkPosition: string, linkDestination: () => string): Tracking => (restored ? linkClickAttrs(linkName, linkPosition, linkDestination()) : {});

  const resetRun = () => {
    setAnswers({});
    setCursor(0);
    prevCursorRef.current = null;
  };

  /* Reached from the first question's Close and from the dialog's own close and
     Escape. Leaving mid-quiz discards the run; a completed one is kept. */
  const closeModal = () => {
    if (phase !== "results") {
      resetRun();
    }
    setModalOpen(false);
  };

  const openInline = () => {
    setInlineStarted(true);
    focusQuestionRef.current = true;
  };

  const toggleOption = (optionId: string) => {
    if (!current) {
      return;
    }
    setAnswers(previous => {
      const picked = previous[current.id] || [];
      let next: string[];
      if (current.mode === "single") {
        next = [optionId];
      } else if (picked.includes(optionId)) {
        next = picked.filter(id => id !== optionId);
      } else {
        next = [...picked, optionId];
      }
      return {
        ...previous,
        [current.id]: next
      };
    });
  };

  const handleNext = () => {
    /* Enforced here as well as on the disabled button, so no other route into
       this handler can skip a step or report an answer for one. */
    const picked = (current && answers[current.id]) || [];
    if (!current || current.options.filter(option => picked.includes(option.id)).length === 0) {
      return;
    }
    fireQuizQuestionStatus({
      status: "answered",
      progressLabel,
      questionLabel: current.label
    });
    if (isLast) {
      armComplete();
      focusResultsRef.current = true;
      setPhase("results");
    } else {
      setCursor(clampedCursor + 1);
    }
  };

  const handleStartOver = () => {
    clearSession(sessionKey);
    /* A restored run reached its card without the entry button, so without
       this the banner would come back instead of the first question. */
    if (!isModal) {
      setInlineStarted(true);
    }
    resetRun();
    focusQuestionRef.current = true;
    setPhase("ask");
  };

  const stepSecondary = (): SecondaryAction | undefined => {
    if (clampedCursor > 0) {
      return {
        label: backLabel,
        ariaLabel: ctaAriaLabel(backCtaAriaLabel),
        onClick: () => setCursor(Math.max(0, clampedCursor - 1))
      };
    }
    if (isModal) {
      return {
        label: closeLabel,
        ariaLabel: closeAriaLabel,
        onClick: closeModal
      };
    }
    return undefined;
  };

  const resultsPosition = recommendationLinkPosition(isModal);
  const priceCopy: PriceCopy = {
    promo: priceCopyPromo,
    ongoing: priceCopyOngoing,
    nextTier: priceCopyNextTier
  };

  /* Inline only, authored per pack: the Signature and genre cards point at
     different lineups. */
  const secondaryCtaFor = (key: OrderedCard["key"]) => {
    const signature = key === "broad";
    const url = isModal ? null : safeCtaUrl(signature ? secondaryCtaSignatureUrl : secondaryCtaGenreUrl);
    if (!url) {
      return undefined;
    }
    const authored = signature ? secondaryCtaSignatureLabel : secondaryCtaGenreLabel;
    const label = String(authored ?? "").trim() || DEFAULT_SECONDARY_CTA_LABEL;
    return {
      label,
      ariaLabel: ctaAriaLabel(signature ? secondaryCtaSignatureAriaLabel : secondaryCtaGenreAriaLabel),
      url,
      variant: secondaryTreatment,
      tracking: linkClickAttrs(label, resultsPosition, absoluteCtaUrl(url))
    };
  };

  const renderQuestion = () => {
    if (!current) {
      return null;
    }
    const optionPosition = optionLinkPosition(isModal, progressLabel, current.label);
    return (
      <QuestionStep
        advanceAriaLabel={ctaAriaLabel(isLast ? resultsCtaAriaLabel : nextCtaAriaLabel)}
        advanceLabel={isLast ? resultsCtaLabel : nextLabel}
        advanceVariant={nextTreatment}
        headingRef={headingRef}
        navTracking={linkName => tracked(linkName, navLinkPosition(isModal, current.label), currentPageUrl)}
        onNext={handleNext}
        onToggle={toggleOption}
        optionTracking={linkName => tracked(linkName, optionPosition, currentPageUrl)}
        progressText={progressLabel}
        question={current}
        questionLevel={questionAs}
        questionVariant={questionStyle}
        scope={scope}
        secondary={stepSecondary()}
        secondaryVariant={backTreatment}
        selectHelper={current.mode === "single" ? selectOneLabel : selectAllLabel}
        selected={answers[current.id] || []}
      />
    );
  };

  const renderResults = () => {
    if (!result) {
      return null;
    }
    const startOverTracking = tracked(startOverLabel, resultsPosition, currentPageUrl);
    return (
      <div className={styles.results}>
        {/* Text takes no ref or tabIndex, so the focus target wraps it. */}
        <div ref={resultsHeadingRef} tabIndex={-1}>
          <Text as={resultsHeadingAs} variant={resultsHeadingStyle}>
            {resultsTitle}
          </Text>
        </div>
        <div className={styles.resultsBody}>
          <div className={styles.cards}>
            {orderedCards.map((card, index) => (
              <ResultCard
                cardKey={card.key}
                channels={card.channels}
                ctaAriaLabel={ctaAriaLabel(primaryCtaAriaLabel)}
                ctaLabel={primaryCtaLabel}
                ctaTracking={linkClickAttrs(primaryCtaLabel, resultsPosition, absoluteCtaUrl(card.ctaUrl))}
                ctaUrl={card.ctaUrl}
                key={card.key}
                legal={priceLegal}
                nextTier={card.nextTier}
                onCtaClick={() => fireSelectItem({
                  itemName: card.title,
                  index,
                  price: card.price,
                  itemListName: ITEM_LIST_NAME
                })}
                price={card.price}
                priceCopy={priceCopy}
                primaryVariant={primaryTreatment}
                promoKnown={card.promoKnown}
                promoMonths={card.promoMonths}
                regularPrice={card.regularPrice}
                scope={scope}
                secondaryCta={secondaryCtaFor(card.key)}
                tagLabel={index === 0 ? primaryTagLabel : undefined}
                title={card.title}
                titleLevel={titleAs}
                titleVariant={titleStyle}
              />
            ))}
          </div>
          <TrackingSlot tracking={startOverTracking}>
            <Button
              ariaLabel={ctaAriaLabel(startOverCtaAriaLabel)}
              onClick={handleStartOver}
              size="medium"
              startIcon={<StartOverIcon />}
              variant={startOverTreatment}
            >
              {startOverLabel}
            </Button>
          </TrackingSlot>
        </div>
      </div>
    );
  };

  const quizBody = phase === "results" ? renderResults() : renderQuestion();
  const entryTracking = tracked(entryCtaLabel, quizLinkPosition(isModal), isModal ? quizVirtualUrl : currentPageUrl);

  const entryBanner = (
    <div className={styles.entry}>
      <div className={styles.entryCopy}>
        {/* The frame binds --text-inverse-primary, white, on both lines. */}
        {heading && (
          <Text as={headingAs} tone="inverse" variant={headingStyle} weight="medium">
            {heading}
          </Text>
        )}
        {subhead && (
          <Text as={subheadAs} tone="inverse" variant={subheadStyle}>
            {subhead}
          </Text>
        )}
      </div>
      <TrackingSlot tracking={entryTracking}>
        <Button
          ariaLabel={ctaAriaLabel(entryCtaAriaLabel)}
          onClick={isModal ? () => setModalOpen(true) : openInline}
          size="large"
          variant={entryTreatment}
        >
          {entryCtaLabel}
        </Button>
      </TrackingSlot>
    </div>
  );

  if (!isModal) {
    let view: "entry" | "ask" | "results" = "entry";
    if (inlineOpen) {
      view = phase;
    }
    const HeadingStandIn = headingAs;
    return (
      <section className={styles.root} id={id}>
        <div className={styles.panel} data-view={view}>
          {backdrop}
          {inlineOpen ? (
            <div className={styles.quiz}>
              {/* The banner's heading has gone; the results screen brings its own. */}
              {heading && phase !== "results" && <HeadingStandIn className="dds-sr-only">{heading}</HeadingStandIn>}
              {quizBody}
            </div>
          ) : entryBanner}
        </div>
      </section>
    );
  }

  return (
    <section className={styles.root} id={id}>
      <div className={styles.panel} data-view="entry">
        {/* One backdrop at a time: the dialog draws its own while open. */}
        {!modalOpen && backdrop}
        {entryBanner}
      </div>
      <Modal
        ariaLabel={dialogLabel}
        closeLabel={closeAriaLabel ?? closeLabel}
        onClose={closeModal}
        open={modalOpen}
        size="extended"
      >
        {modalOpen ? (
          <>
            {backdrop}
            <div className={styles.quiz}>{quizBody}</div>
          </>
        ) : null}
      </Modal>
    </section>
  );
}
export const ProductRecommenderClient = withSafeRender(ProductRecommenderClientImpl, "ProductRecommenderClient");
